import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';

/**
 * Circular avatar. Renders `src` as a cover-fit image; when `src` is null/empty it
 * falls back to either the uppercase initials derived from `name` (first + last word,
 * `fallback="initials"`, default) or a `pi pi-user` silhouette (`fallback="icon"`).
 *
 * Colors are theme-aware via `--p-surface-*` and overridable per context through the
 * `--crm-avatar-bg` / `--crm-avatar-color` custom properties. `size` (px) drives both
 * the box and the initials font-size.
 *
 * @example
 * <crm-avatar [src]="user.avatarUrl" [name]="fullName()" [size]="28" />
 */
@Component({
  selector: 'crm-avatar',
  template: `
    @if (src(); as url) {
      <img class="crm-avatar__img" [src]="url" [alt]="name()" />
    } @else if (fallback() === 'icon') {
      <i class="pi pi-user crm-avatar__icon" aria-hidden="true"></i>
    } @else {
      <span class="crm-avatar__initials" aria-hidden="true">{{ initials() }}</span>
    }
  `,
  styleUrl: './avatar.scss',
  host: {
    class: 'crm-avatar',
    '[class.crm-avatar--icon]': "fallback() === 'icon'",
    '[style.--crm-avatar-size]': 'sizePx()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Avatar {
  readonly src = input<string | null>(null);
  readonly name = input<string>('');
  readonly size = input<number>(40);
  readonly fallback = input<'initials' | 'icon'>('initials');

  protected readonly sizePx = computed(() => `${this.size()}px`);

  protected readonly initials = computed(() => {
    const parts = this.name().trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    const first = parts[0].charAt(0);
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
  });
}
